import { auth } from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Firebase Auth가 초기화될 때까지 대기
 */
function waitForAuth(): Promise<void> {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve();
      return;
    }

    let settled = false;
    const unsubscribe = onAuthStateChanged(auth, () => {
      if (!settled) {
        settled = true;
        unsubscribe();
        resolve();
      }
    });

    // 최대 5초 대기 후 리스너 정리
    setTimeout(() => {
      if (!settled) {
        settled = true;
        unsubscribe();
        resolve();
      }
    }, 5000);
  });
}

/**
 * 인증된 API 호출 헬퍼
 * Firebase Auth에서 현재 사용자의 최신 토큰을 가져와서 요청에 포함
 */
export async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  // Auth 초기화 대기
  if (!auth.currentUser) {
    await waitForAuth();
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('로그인이 필요합니다.');
  }

  const token = await user.getIdToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  // Content-Type은 기본 application/json, 이미 설정된 경우 덮어쓰지 않음
  const existingHeaders = options?.headers as Record<string, string> | undefined;
  if (!existingHeaders?.['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...existingHeaders,
      ...headers,
    },
  });

  // 401 → 토큰 만료 → 자동 로그아웃 (현재 경로 보존)
  if (response.status === 401) {
    const { signOut } = await import('firebase/auth');
    await signOut(auth).catch(() => {});
    localStorage.removeItem('firebase_token');
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/signup') {
      window.location.href = '/login?redirect=' + encodeURIComponent(currentPath);
    }
  }

  return response;
}
